import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Mail } from 'lucide-react'
import type { SessionContact } from '@/services/chatbotService'

export function SessionContactCard({ contact }: { contact: SessionContact }) {
  const { t } = useTranslation('dashboard')
  const headingId = useId()
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'success' | 'error'>('idle')
  const email = contact.email?.trim() || ''
  // Only activate email actions for a single mailbox, never a URL or mail header.
  const validEmail = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i.test(email)

  const copyEmail = async () => {
    setCopyStatus('copying')
    try {
      await navigator.clipboard.writeText(email)
      setCopyStatus('success')
    } catch {
      setCopyStatus('error')
    }
  }

  return (
    <section aria-labelledby={headingId} className="mt-3 min-w-0 rounded-xl border-2 border-primary-300 bg-primary-50 p-3 text-gray-900">
      <h4 id={headingId} className="flex items-center gap-2 text-sm font-bold text-primary-800">
        <Mail aria-hidden="true" className="h-4 w-4 shrink-0" />
        {t('chatBotSessionContact')}
      </h4>
      <p className="mt-2 text-sm font-semibold break-words">{contact.sessionTitle}</p>
      <p className="mt-1 text-xs text-gray-600">{t('chatBotSessionId', { id: contact.sessionId })}</p>
      {contact.name && <p className="mt-2 text-sm font-semibold">{contact.name}</p>}
      {contact.phone && <p className="mt-1 text-sm">{contact.phone}</p>}
      {!email && <p className="mt-3 text-sm text-gray-700">{t('chatBotContactEmailUnavailable')}</p>}
      {email && !validEmail && <p className="mt-3 break-all text-base font-bold">{email}</p>}
      {validEmail && (
        <>
          <a href={`mailto:${encodeURIComponent(email).replace(/%40/g, '@')}`}
            className="mt-3 block break-all text-base font-bold text-primary-700 underline hover:text-primary-800 focus-visible:outline-2 focus-visible:outline-primary-500">
            {email}
          </a>
          <button type="button" onClick={() => void copyEmail()} disabled={copyStatus === 'copying'}
            aria-label={t('chatBotCopyEmailForSession', { title: contact.sessionTitle, id: contact.sessionId })}
            className="mt-2 flex min-h-11 items-center gap-2 rounded-lg border border-primary-300 bg-white px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50">
            <Copy aria-hidden="true" className="h-4 w-4 shrink-0" />
            {t('chatBotCopyEmail')}
          </button>
          <p role="status" aria-live="polite" aria-atomic="true" className="mt-1 text-xs text-primary-800">
            {copyStatus === 'success' && t('chatBotEmailCopied')}
            {copyStatus === 'error' && t('chatBotEmailCopyFailed')}
          </p>
        </>
      )}
    </section>
  )
}
